import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { CloudUpload, CloseRounded } from "@mui/icons-material";
import { MAX_DESCRIPTION_LEN, MAX_TITLE_LEN } from "../../../services/schemas/ticketSchemas";
import { validateFileByMimeAndSignature } from "../../../services/fileValidation";

function formatBytes(bytes) {
  if (typeof bytes !== "number" || Number.isNaN(bytes)) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
}

export function NewTicketForm({ onSubmit, onCancel, disabled, isSubmitting, showIntro = true }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");

  const isFormValid = useMemo(() => {
    const t = title.trim();
    const d = description.trim();
    return Boolean(t) && Boolean(d) && t.length <= MAX_TITLE_LEN && d.length <= MAX_DESCRIPTION_LEN;
  }, [title, description]);

  const handleReset = () => {
    setTitle("");
    setDescription("");
    setFile(null);
    setAttachmentError("");
  };

  const handleFileChange = async (event) => {
    if (disabled) return;
    const selected = event.target.files?.[0];
    if (!selected) return;
    event.target.value = null;

    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (selected.size > 2 * 1024 * 1024) {
      setFile(null);
      setAttachmentError("Allegato non valido (max 2MB, consentiti: JPEG/PNG/PDF)");
      return;
    }

    const mimeValidation = await validateFileByMimeAndSignature(selected, allowedTypes);
    if (!mimeValidation.ok) {
      setFile(null);
      setAttachmentError(mimeValidation.message);
      return;
    }

    setAttachmentError("");
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (disabled || !isFormValid) return;
    await onSubmit({ title, description, file });
    handleReset();
  };

  const handleCancel = () => {
    handleReset();
    onCancel?.();
  };

  return (
    <Box>
      {showIntro ? (
        <Typography sx={{ fontFamily: "Rubik", color: "#808080", mb: 3 }}>
          Inserisci oggetto e descrizione della tua segnalazione. Puoi allegare un file (max 2MB, JPEG, PNG o PDF).
        </Typography>
      ) : null}

      <Box display="flex" flexDirection="column" gap={1}>
        <Box display="flex" justifyContent="space-between" alignItems="baseline">
          <Typography sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87" }}>Oggetto*</Typography>
          <Typography sx={{ fontFamily: "Rubik", color: "#808080", fontSize: "0.875rem" }}>
            {title.length}/{MAX_TITLE_LEN}
          </Typography>
        </Box>
        <TextField
          fullWidth
          placeholder="Es. Richiesta assistenza"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={disabled || isSubmitting}
          inputProps={{ maxLength: MAX_TITLE_LEN }}
          sx={{ fontFamily: "Rubik", backgroundColor: "#f0f5ff", borderRadius: "8px" }}
        />
      </Box>

      <Box mt={3} display="flex" flexDirection="column" gap={1}>
        <Box display="flex" justifyContent="space-between" alignItems="baseline">
          <Typography sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87" }}>Descrizione*</Typography>
          <Typography sx={{ fontFamily: "Rubik", color: "#808080", fontSize: "0.875rem" }}>
            {description.length}/{MAX_DESCRIPTION_LEN}
          </Typography>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={5}
          placeholder="Descrivi il problema o la richiesta"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={disabled || isSubmitting}
          inputProps={{ maxLength: MAX_DESCRIPTION_LEN }}
          sx={{ fontFamily: "Rubik", backgroundColor: "#f0f5ff", borderRadius: "8px" }}
        />
      </Box>

      <Box mt={3} sx={{ width: { xs: "100%", md: "50%" } }}>
        <Typography sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87", mb: 0.5 }}>
          Allegato (opzionale)
        </Typography>
        <Button
          component="label"
          variant="outlined"
          disabled={disabled || isSubmitting}
          endIcon={<CloudUpload />}
          sx={{
            fontFamily: "Rubik",
            textTransform: "none",
            backgroundColor: "#f0f5ff",
            color: "#0b3d87",
            borderRadius: "8px",
            height: "56px",
            justifyContent: "space-between",
            px: 2,
            width: "100%",
          }}
        >
          {file ? `${file.name} (${formatBytes(file.size)})` : "Carica file"}
          <input type="file" hidden accept="image/jpeg,image/png,application/pdf" onChange={handleFileChange} />
        </Button>
        {attachmentError ? (
          <Typography variant="caption" color="error.main" sx={{ mt: 0.75, display: "block", fontFamily: "Rubik" }}>
            {attachmentError}
          </Typography>
        ) : null}
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
        <Button
          variant="text"
          onClick={handleCancel}
          disabled={isSubmitting || disabled}
          sx={{ fontFamily: "Rubik", color: "#0b3d87", fontWeight: "bold", textTransform: "none" }}
        >
          Annulla
        </Button>
        <Button
          variant="contained"
          disabled={isSubmitting || disabled || !isFormValid}
          onClick={handleSubmit}
          sx={{
            fontFamily: "Rubik",
            backgroundColor: "#0b3d87",
            fontWeight: "bold",
            textTransform: "none",
            borderRadius: "30px",
            px: 3,
          }}
        >
          {isSubmitting ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : "Invia ticket"}
        </Button>
      </Box>
    </Box>
  );
}

NewTicketForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  showIntro: PropTypes.bool,
};

NewTicketForm.defaultProps = {
  onCancel: undefined,
  disabled: false,
  isSubmitting: false,
  showIntro: true,
};

export function NewTicketPanel({ onSubmit, disabled, isSubmitting }) {
  return (
    <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: "12px", backgroundColor: "#fff", mb: 2 }}>
      <Typography sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87", fontSize: 18, mb: 2 }}>
        Nuovo ticket
      </Typography>
      <NewTicketForm onSubmit={onSubmit} disabled={disabled} isSubmitting={isSubmitting} />
    </Paper>
  );
}

NewTicketPanel.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
};

NewTicketPanel.defaultProps = {
  disabled: false,
  isSubmitting: false,
};

export function NewTicketDialog({ open, onClose, onSubmit, disabled, isSubmitting }) {
  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: { borderRadius: "12px" },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Rubik",
          fontWeight: "bold",
          color: "#0b3d87",
          pr: 6,
        }}
      >
        Nuovo ticket
        <IconButton
          aria-label="Chiudi"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ position: "absolute", right: 12, top: 12, color: "#64748b" }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <NewTicketForm
          onSubmit={onSubmit}
          onCancel={onClose}
          disabled={disabled}
          isSubmitting={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  );
}

NewTicketDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
};

NewTicketDialog.defaultProps = {
  disabled: false,
  isSubmitting: false,
};
